import { modalStore } from "@/lib/stores/modals";
import { cn } from "@/lib/utils";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import Announcement from "../business/announcementModal";
import {
	AnnouncementIcon,
	DepositIcon,
	DepositLightIcon,
	ExploreIcon,
	ExploreLightIcon,
	HostPublishIcon,
	InviteIcon,
	InviteLightIcon,
	PPIcon,
} from "../icons/header";
import { Account } from "./account";
import { EditBtn } from "./edit-btn";
import { LanguageSelector } from "./language-selector";
import { ThemeToggle } from "./theme-toggle";
import "./header.css";
import { authStore } from "@/lib/auth/store";
import { m } from "@/paraglide/messages";
import { localizeHref } from "@/paraglide/runtime";
import { GemIcon2, MenuIcon } from "../icons";

import { CardTooltip } from "../ui/card-tooltip";

import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { useLanguage } from "@/hooks/useLanguage";
import { useMediaQuery } from "@/hooks/useMedia";
import { LoginModal } from "@/lib/auth/login-modal";
import { formatThousands } from "@/lib/utils";
import NiceModal from "@ebay/nice-modal-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getAnnouncement } from "../business/announcementModal/api";

function MobileMenu() {
	const [isOpen, setIsOpen] = useState(false);
	const pathname = useLocation().pathname;
	useEffect(() => {
		setIsOpen(false);
	}, [pathname]);
	return (
		<Drawer direction="left" open={isOpen} onOpenChange={setIsOpen}>
			<DrawerTrigger>
				<MenuIcon className="w-6 h-6" />
			</DrawerTrigger>
			<DrawerContent className="w-3/4 rounded-r-[20px] flex flex-col justify-between pb-10">
				<Navs />
				<div className="flex justify-center mt-10">
					<NavActions />
				</div>
			</DrawerContent>
		</Drawer>
	);
}

function Navs() {
	const location = useLocation();
	const user = useStore(authStore, (s) => s.user);
	return (
		<nav className="flex gap-6 max-md:flex-col ml-10 max-md:ml-8 max-md:mt-19">
			<NavItem
				to={localizeHref("/")}
				active={`${location.pathname}/` === localizeHref("/")}
				icon={ExploreIcon}
				activeIcon={ExploreLightIcon}
				label={m.explore()}
			/>
			<EditBtn />
			<NavAction
				onClick={() =>
					user
						? modalStore.setDepositModalOpen(true)
						: NiceModal.show(LoginModal)
				}
				icon={DepositIcon}
				activeIcon={DepositLightIcon}
				label={m.deposit()}
			/>
			<NavAction
				onClick={() =>
					user
						? modalStore.setInviteModalOpen(true)
						: NiceModal.show(LoginModal)
				}
				icon={InviteIcon}
				activeIcon={InviteLightIcon}
				label={m.invite()}
			/>
		</nav>
	);
}

function NavActions() {
	const lang = useLanguage();
	const user = useStore(authStore, (s) => s.user);
	const { data: announcement } = useQuery({
		queryKey: ["announcement"],
		queryFn: () => getAnnouncement({ page_num: 1, page_size: 20 }),
		enabled: !!user,
	});

	return (
		<div className="flex items-center gap-7 mr-2 *:cursor-pointer">
			<PPIcon
				className="icon-wrapper"
				onClick={() => modalStore.setPPModalOpen(true)}
			/>
			<CardTooltip
				trigger={
					<a
						href={`https://peachlust.github.io/?lang=${lang}`}
						target="_blank"
						rel="noreferrer"
					>
						<HostPublishIcon />
					</a>
				}
				label={m.host_publish()}
			/>
			<div className="relative">
				{announcement?.data && !announcement?.data?.has_read && (
					<span className="absolute right-[4px] top-[1px] ml-2 w-[6px] h-[6px] bg-main1 rounded-full inline-block flex-shrink-0" />
				)}
				<AnnouncementIcon
					className="icon-wrapper"
					onClick={() =>
						user
							? modalStore.setAnnouncementModalOpen(true)
							: NiceModal.show(LoginModal)
					}
				/>
			</div>

			<ThemeToggle />
			<LanguageSelector />
		</div>
	);
}

export function Header() {
	const user = useStore(authStore, (s) => s.user);
	const max1280 = useMediaQuery("(max-width: 1280px)");
	const navigate = useNavigate();
	return (
		<div className="h-19 text-t1 shrink-0 flex flex-row items-center px-10 max-md:px-5">
			<Link
				to={localizeHref("/")}
				className="flex items-center gap-2 flex-shrink-0"
			>
				<img src="/logo.svg" alt="logo" className="h-10" />
			</Link>
			<div className="max-md:hidden">
				<Navs />
			</div>

			<div className="ml-auto flex items-center md:gap-7 gap-3.5">
				<div className="max-md:hidden">
					<NavActions />
				</div>
				{!max1280 && (
					<div
						className="flex items-center gap-1 bg-bg2 rounded-full px-3.5 py-1.5 cursor-pointer max-md:hidden"
						onClick={(e) => {
							e.preventDefault();
							if (user) {
								modalStore.setDepositModalOpen(true);
							} else {
								NiceModal.show(LoginModal);
							}
						}}
					>
						<span className="inline-block max-w-[120px] truncate">
							{m.my_diamond()}
						</span>
						<p className="flex items-center gap-1">
							<GemIcon2 />
							<span
								className="hover:underline"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									if (user) {
										navigate({ to: localizeHref("/profile/gem") });
									} else {
										NiceModal.show(LoginModal);
									}
								}}
							>
								{user ? formatThousands(user?.gem ?? 0) : "-"}
							</span>
						</p>
					</div>
				)}

				<Account />
				<div className="max-md:block hidden">
					<MobileMenu />
				</div>
			</div>
			<Announcement />
		</div>
	);
}

interface NavItemProps {
	to: string;
	active: boolean;
	icon: React.ComponentType<any>;
	activeIcon: React.ComponentType<any>;
	label: string;
}

function NavItem({
	to,
	active,
	icon: Icon,
	activeIcon: ActiveIcon,
	label,
}: NavItemProps) {
	return (
		<Link
			to={to}
			className={cn(
				"flex items-center gap-2 group hover:text-main1",
				active && "text-main1",
			)}
		>
			<Icon
				className={cn("icon-wrapper group-hover:hidden", active && "hidden")}
			/>
			<ActiveIcon
				className={cn(
					"icon-wrapper hidden group-hover:block",
					active && "block",
				)}
			/>
			<span className="inline-block max-w-[80px] truncate">{label}</span>
		</Link>
	);
}

interface NavActionProps {
	onClick: () => void;
	icon: React.ComponentType<any>;
	activeIcon: React.ComponentType<any>;
	label: string;
}

function NavAction({
	onClick,
	icon: Icon,
	activeIcon: ActiveIcon,
	label,
}: NavActionProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="flex items-center cursor-pointer gap-2 group hover:text-main1"
		>
			<Icon className="icon-wrapper group-hover:hidden" />
			<ActiveIcon className="icon-wrapper hidden group-hover:block" />
			<span className="inline-block max-w-[80px] truncate">{label}</span>
		</button>
	);
}
